import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export type ContentAlertSeverity = 'info' | 'warning' | 'critical';
export type ContentAlertStatus = 'draft' | 'scheduled' | 'published' | 'archived';
export type ContentAlertSourceType = 'platform' | 'organization' | 'authority';
export type ContentAlertScopeType =
  | 'global' | 'country' | 'region' | 'waterbody' | 'spot' | 'organization' | 'poi';
export type ContentAlertMessageType =
  | 'restriction' | 'closure' | 'warning' | 'notice' | 'maintenance';

export interface ContentAlert {
  id: string;
  slug: string | null;
  title_json: Record<string, string>;
  summary_json: Record<string, string>;
  body_json: Record<string, string>;
  message_type: ContentAlertMessageType;
  severity: ContentAlertSeverity;
  status: ContentAlertStatus;
  scope_type: ContentAlertScopeType;
  scope_ref_id: string | null;
  applies_to_listing: boolean;
  applies_to_detail: boolean;
  source_type: ContentAlertSourceType;
  source_organization_id: string | null;
  source_note: string | null;
  valid_from: string | null;
  valid_until: string | null;
  recurrence_type: 'none' | 'weekly' | 'monthly' | 'annual';
  recurrence_start_month: number | null;
  recurrence_start_day: number | null;
  recurrence_end_month: number | null;
  recurrence_end_day: number | null;
  recurrence_weekdays: number[] | null;
  recurrence_days_of_month: number[] | null;
  published_at: string | null;
  cta_label_json: Record<string, string> | null;
  cta_url: string | null;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

/** Inheritance-Marker — wird vom Hook ergänzt, ist nicht in der DB. */
export interface ContentAlertWithInheritance extends ContentAlert {
  /** Quelle der Vererbung: null = direkt am Spot, sonst Geo-Scope. */
  inheritedFrom: null | {
    scope_type: 'waterbody' | 'region' | 'country';
    label: string;
  };
}

/** 7-Tage-Vorlauf: Meldungen sollen schon eine Woche vor Beginn sichtbar sein. */
const LEAD_DAYS = 7;
const DAY_MS = 86_400_000;

function isAnnualWindowActive(
  startM: number | null, startD: number | null,
  endM: number | null, endD: number | null,
  refDate: Date = new Date(),
): boolean {
  if (!startM || !startD || !endM || !endD) return false;
  const today = (refDate.getMonth() + 1) * 100 + refDate.getDate();
  const start = startM * 100 + startD;
  const end = endM * 100 + endD;
  return start <= end ? today >= start && today <= end : today >= start || today <= end;
}

/** ISO-Wochentag: Mo=1 .. So=7. */
function isoWeekday(d: Date): number {
  const js = d.getDay(); // So=0 .. Sa=6
  return js === 0 ? 7 : js;
}

function isCurrentlyValid(
  alert: ContentAlert,
): boolean {
  if (alert.status !== 'published') return false;
  const now = new Date();
  const leadCutoff = new Date(now.getTime() + LEAD_DAYS * DAY_MS);
  // Vorlauf: Meldung schon 7 Tage vor Start zeigen
  if (alert.valid_from && new Date(alert.valid_from) > leadCutoff) return false;
  // Nach Ablauf hart ausblenden
  if (alert.valid_until && new Date(alert.valid_until) <= now) return false;

  switch (alert.recurrence_type) {
    case 'none':
      return true;
    case 'annual': {
      // im Fenster ODER innerhalb 7 Tage davor
      for (let d = 0; d <= LEAD_DAYS; d++) {
        const ref = new Date(now.getTime() + d * DAY_MS);
        if (isAnnualWindowActive(
          alert.recurrence_start_month, alert.recurrence_start_day,
          alert.recurrence_end_month,   alert.recurrence_end_day,
          ref,
        )) return true;
      }
      return false;
    }
    case 'weekly': {
      const days = alert.recurrence_weekdays ?? [];
      if (!days.length) return false;
      for (let d = 0; d <= LEAD_DAYS; d++) {
        const ref = new Date(now.getTime() + d * DAY_MS);
        if (days.includes(isoWeekday(ref))) return true;
      }
      return false;
    }
    case 'monthly': {
      const days = alert.recurrence_days_of_month ?? [];
      if (!days.length) return false;
      for (let d = 0; d <= LEAD_DAYS; d++) {
        const ref = new Date(now.getTime() + d * DAY_MS);
        if (days.includes(ref.getDate())) return true;
      }
      return false;
    }
    default:
      return true;
  }
}

/** Public-Read: alle aktuell sichtbaren, gültigen Meldungen. */
export function usePublicContentAlerts() {
  return useQuery({
    queryKey: ['content-alerts', 'public'],
    queryFn: async (): Promise<ContentAlert[]> => {
      const { data, error } = await supabase
        .from('content_alerts' as never)
        .select('*')
        .eq('status', 'published');
      if (error) throw error;
      return ((data ?? []) as unknown as ContentAlert[]).filter(isCurrentlyValid);
    },
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
}

/** Aktive Meldungen für einen Spot (scope_type='spot' + scope_ref_id=spotId). */
/**
 * Aktive Meldungen für einen Spot — inklusive Geo-Vererbung.
 *
 * Aggregiert:
 *   - direkte Spot-Meldungen (`scope_type='spot'`)
 *   - Gewässer-Meldungen für `waterbody_id` des Spots
 *   - Regions-Meldungen für `region_id` (über `place_id`)
 *   - Landes-Meldungen für `country_id` / Ländercode
 *
 * Geo-Meldungen erhalten ein `inheritedFrom`-Label, das im UI als Kontext
 * angezeigt wird ("Gilt für gesamten Bodensee" etc.).
 */
export function useContentAlertsForSpot(spotId: string | undefined): ContentAlertWithInheritance[] {
  const { data: all } = usePublicContentAlerts();
  const { data: hierarchy } = useQuery({
    queryKey: ['spot-geo-hierarchy', spotId],
    enabled: !!spotId,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      if (!spotId) return null;
      const { data: spot } = await supabase
        .from('spots')
        .select('id, waterbody_id, place_id')
        .eq('id', spotId)
        .maybeSingle();
      if (!spot) return null;

      const [wb, place] = await Promise.all([
        spot.waterbody_id
          ? supabase
              .from('waterbodies')
              .select('id, name_json, country_code')
              .eq('id', spot.waterbody_id)
              .maybeSingle()
              .then((r) => r.data)
          : Promise.resolve(null),
        spot.place_id
          ? supabase
              .from('places')
              .select('id, region_id, country_id, display_name_json')
              .eq('id', spot.place_id)
              .maybeSingle()
              .then((r) => r.data)
          : Promise.resolve(null),
      ]);

      const [region, country] = await Promise.all([
        place?.region_id
          ? supabase
              .from('regions')
              .select('id, code, name_de, name_en')
              .eq('id', place.region_id)
              .maybeSingle()
              .then((r) => r.data)
          : Promise.resolve(null),
        place?.country_id
          ? supabase
              .from('countries')
              .select('id, iso2, name_de, name_en')
              .eq('id', place.country_id)
              .maybeSingle()
              .then((r) => r.data)
          : Promise.resolve(null),
      ]);

      return {
        waterbody: wb
          ? { id: String(wb.id), label: pickI18n(wb.name_json) }
          : null,
        region: region
          ? { id: String(region.id), code: region.code, label: region.name_de ?? region.name_en ?? region.code }
          : null,
        country: country
          ? { id: String(country.id), code: country.iso2, label: country.name_de ?? country.name_en ?? country.iso2 }
          : (wb?.country_code ? { id: wb.country_code, code: wb.country_code, label: wb.country_code } : null),
      };
    },
  });

  if (!spotId) return [];
  const alerts = all ?? [];

  const direct: ContentAlertWithInheritance[] = alerts
    .filter((a) => a.scope_type === 'spot' && a.scope_ref_id === spotId)
    .map((a) => ({ ...a, inheritedFrom: null }));

  if (!hierarchy) return direct;

  const inherited: ContentAlertWithInheritance[] = [];
  for (const a of alerts) {
    if (a.scope_type === 'waterbody' && hierarchy.waterbody && a.scope_ref_id === hierarchy.waterbody.id) {
      inherited.push({ ...a, inheritedFrom: { scope_type: 'waterbody', label: hierarchy.waterbody.label } });
    } else if (a.scope_type === 'region' && hierarchy.region && (a.scope_ref_id === hierarchy.region.id || a.scope_ref_id === hierarchy.region.code)) {
      inherited.push({ ...a, inheritedFrom: { scope_type: 'region', label: hierarchy.region.label } });
    } else if (a.scope_type === 'country' && hierarchy.country && (a.scope_ref_id === hierarchy.country.id || a.scope_ref_id === hierarchy.country.code)) {
      inherited.push({ ...a, inheritedFrom: { scope_type: 'country', label: hierarchy.country.label } });
    }
  }

  return [...direct, ...inherited];
}

function pickI18n(json: unknown): string {
  if (!json || typeof json !== 'object') return '';
  const obj = json as Record<string, unknown>;
  for (const key of ['de', 'en', 'fr', 'it']) {
    const v = obj[key];
    if (typeof v === 'string' && v.trim()) return v;
  }
  return '';
}

/** Listing-Alerts: Map<spotId, alerts[]>. Nur scope_type='spot' + applies_to_listing. */
export function useContentAlertsListingMap() {
  const { data: all } = usePublicContentAlerts();
  const map = new Map<string, ContentAlert[]>();
  for (const a of all ?? []) {
    if (a.scope_type !== 'spot' || !a.applies_to_listing || !a.scope_ref_id) continue;
    const list = map.get(a.scope_ref_id) ?? [];
    list.push(a);
    map.set(a.scope_ref_id, list);
  }
  return map;
}

/** Globale Meldungen (scope_type='global'). */
export function useGlobalContentAlerts() {
  const { data: all } = usePublicContentAlerts();
  return (all ?? []).filter((a) => a.scope_type === 'global');
}

/** Admin-Read: ALLE Meldungen unabhängig vom Status (RLS prüft Admin-Rolle). */
export function useAdminContentAlerts() {
  return useQuery({
    queryKey: ['content-alerts', 'admin'],
    queryFn: async (): Promise<ContentAlert[]> => {
      const { data, error } = await supabase
        .from('content_alerts' as never)
        .select('*')
        .order('updated_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as ContentAlert[];
    },
    staleTime: 30_000,
  });
}